import { create } from 'zustand'
import { User as FirebaseUser } from 'firebase/auth'
import { User, OrganizationMember } from '@prepro/shared'
import { CHOYCE_CREATIVE_ORG_ID } from '@/config/organization'

/**
 * MFA Challenge state during login
 */
interface MFAChallengeState {
  required: boolean
  userId: string
  email: string
  totpEnabled: boolean
  passkeyEnabled: boolean
}

interface AuthState {
  user: User | null
  firebaseUser: FirebaseUser | null
  currentOrganizationId: string | null
  organizationMemberships: OrganizationMember[]
  loading: boolean
  initialized: boolean
  error: string | null
  // Client portal preview mode
  previewClientId: string | null
  previewClientName: string | null
  // MFA challenge state
  mfaChallenge: MFAChallengeState | null
  pendingCredentials: { email: string; password: string; isGoogleAuth?: boolean } | null
  setUser: (user: User | null, firebaseUser: FirebaseUser | null) => void
  setCurrentOrganization: (orgId: string | null) => void
  setOrganizationMemberships: (memberships: OrganizationMember[]) => void
  setLoading: (loading: boolean) => void
  setInitialized: (initialized: boolean) => void
  setError: (error: string | null) => void
  setPreviewClient: (clientId: string | null, clientName?: string | null) => void
  // MFA actions
  setMFAChallenge: (challenge: MFAChallengeState | null) => void
  setPendingCredentials: (credentials: { email: string; password: string; isGoogleAuth?: boolean } | null) => void
  clearMFAState: () => void
  reset: () => void
  reloadUser: () => Promise<void>
  signOut: () => Promise<void>
}

export const useAuth = () => useAuthStore()

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  firebaseUser: null,
  // Always use Choyce Creative org - single tenant mode
  currentOrganizationId: CHOYCE_CREATIVE_ORG_ID,
  organizationMemberships: [],
  loading: true,
  initialized: false,
  error: null,
  previewClientId: null,
  previewClientName: null,
  // MFA state
  mfaChallenge: null,
  pendingCredentials: null,
  setUser: (user, firebaseUser) => set({ user, firebaseUser, error: null }),
  setCurrentOrganization: (orgId) => set({ currentOrganizationId: orgId }),
  setOrganizationMemberships: (memberships) => set({ organizationMemberships: memberships }),
  setLoading: (loading) => set({ loading }),
  setInitialized: (initialized) => set({ initialized }),
  setError: (error) => set({ error }),
  setPreviewClient: (clientId, clientName = null) => set({ previewClientId: clientId, previewClientName: clientName }),
  // MFA actions
  setMFAChallenge: (challenge) => set({ mfaChallenge: challenge }),
  setPendingCredentials: (credentials) => set({ pendingCredentials: credentials }),
  clearMFAState: () => set({ mfaChallenge: null, pendingCredentials: null }),
  reset: () =>
    set({
      user: null,
      firebaseUser: null,
      // Keep Choyce Creative org even after reset - single tenant mode
      currentOrganizationId: CHOYCE_CREATIVE_ORG_ID,
      organizationMemberships: [],
      loading: false,
      initialized: true,
      error: null,
      previewClientId: null,
      previewClientName: null,
      mfaChallenge: null,
      pendingCredentials: null,
    }),
  reloadUser: async () => {
    const { firebaseUser } = get()
    if (firebaseUser) {
      const { authService } = await import('@/services/auth')
      await authService.syncUserData(firebaseUser)
    }
  },
  signOut: async () => {
    const { authService } = await import('@/services/auth')
    await authService.signOut()
  },
}))